import type React from "react";
import {useCallback, useRef} from "react";

import useMyCamera from "~/contexts/camera/useMyCamera";
import {useGlobalEvent} from "~/contexts/event/GlobalEventBusContext";

export const usePanningControls = () => {
  const {targetPosition, targetZoom} = useMyCamera();

  // Whether a panning gesture is active.
  const isPanningRef = useRef<boolean>(false);
  // The initial pointer position (average for touch or direct mouse event) when panning begins.
  const initialGestureRef = useRef<{x: number; y: number}>({x: 0, y: 0});
  // The camera target position at the start of the gesture.
  const initialTargetRef = useRef<{x: number; y: number}>({x: 0, y: 0});
  // For touch, track active pointers.
  const activeTouchPointers = useRef(
    new Map<number, {clientX: number; clientY: number}>(),
  );

  // Helper to compute the average (x,y) of all active touch pointers.
  const averageTouchPositions = useCallback(() => {
    let sumX = 0,
      sumY = 0,
      count = 0;
    activeTouchPointers.current.forEach((pos) => {
      sumX += pos.clientX;
      sumY += pos.clientY;
      count++;
    });
    return {x: sumX / count, y: sumY / count};
  }, []);

  // Handle pointer down events:
  // • For mouse: if it's a right-click (button 2), start panning.
  // • For touch: record the pointer and, if 2+ touches exist, start panning.
  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.pointerType === "mouse" && event.button === 2) {
        // Start panning with right mouse button.
        isPanningRef.current = true;
        initialGestureRef.current = {x: event.clientX, y: event.clientY};
        initialTargetRef.current = {
          x: targetPosition.current.x,
          y: targetPosition.current.y,
        };
      } else if (event.pointerType === "touch") {
        // Record this touch pointer.
        activeTouchPointers.current.set(event.pointerId, {
          clientX: event.clientX,
          clientY: event.clientY,
        });
        // If we have two or more touches and we’re not already panning, start.
        if (activeTouchPointers.current.size >= 2 && !isPanningRef.current) {
          const avg = averageTouchPositions();
          isPanningRef.current = true;
          initialGestureRef.current = avg;
          initialTargetRef.current = {
            x: targetPosition.current.x,
            y: targetPosition.current.y,
          };
        }
      }
    },
    [targetPosition, averageTouchPositions],
  );

  // Handle pointer move events:
  // • For mouse: update panning based on movement.
  // • For touch: update the pointer's position in the map, compute the average,
  //   and update panning accordingly.
  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.pointerType === "mouse") {
        if (isPanningRef.current) {
          const deltaX =
            (event.clientX - initialGestureRef.current.x) / targetZoom.current;
          const deltaY =
            (event.clientY - initialGestureRef.current.y) / targetZoom.current;
          targetPosition.current.x = initialTargetRef.current.x - deltaX;
          targetPosition.current.y = initialTargetRef.current.y + deltaY;
        }
      } else if (event.pointerType === "touch") {
        // Update the stored position for this touch pointer.
        activeTouchPointers.current.set(event.pointerId, {
          clientX: event.clientX,
          clientY: event.clientY,
        });
        if (isPanningRef.current) {
          const avg = averageTouchPositions();
          const deltaX =
            (avg.x - initialGestureRef.current.x) / targetZoom.current;
          const deltaY =
            (avg.y - initialGestureRef.current.y) / targetZoom.current;
          targetPosition.current.x = initialTargetRef.current.x - deltaX;
          targetPosition.current.y = initialTargetRef.current.y + deltaY;
        }
      }
    },
    [targetZoom, targetPosition, averageTouchPositions],
  );

  // Handle pointer up events:
  // • For mouse: if right button is released, end panning.
  // • For touch: remove the pointer from the map and end panning if fewer than 2 remain.
  const handlePointerUp = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.pointerType === "mouse" && event.button === 2) {
        isPanningRef.current = false;
      } else if (event.pointerType === "touch") {
        activeTouchPointers.current.delete(event.pointerId);
        if (activeTouchPointers.current.size < 2) {
          isPanningRef.current = false;
        }
      }
    },
    [],
  );

  // Handle pointer cancel events (similar to pointer up).
  const handlePointerCancel = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.pointerType === "mouse" && event.button === 2) {
        isPanningRef.current = false;
      } else if (event.pointerType === "touch") {
        activeTouchPointers.current.delete(event.pointerId);
        if (activeTouchPointers.current.size < 2) {
          isPanningRef.current = false;
        }
      }
    },
    [],
  );

  // Register panning events through the global event bus.
  useGlobalEvent("pointerdown", handlePointerDown);
  useGlobalEvent("pointermove", handlePointerMove);
  useGlobalEvent("pointerup", handlePointerUp);
  useGlobalEvent("pointercancel", handlePointerCancel);
};
